import {
  getAggregateInsuranceStatus,
  LiabilityStatusEnum,
  LicenseStatusEnum,
} from "../../shared/utils"
import { PublicProfileSlideContent } from "../../../domain/CMS/schema"
import { type BannerType } from "../../shared/components/Banner"

/**
 * Determines the banner type based on liability status
 */
export const getLiabilityBannerType = (
  status: LiabilityStatusEnum,
): BannerType => {
  if (status === LiabilityStatusEnum.PENDING) {
    return "info" as BannerType
  }
  return "error" as BannerType
}

interface PracticeLiabilityBannerData {
  showLiabilityBanner: boolean
  liabilityText: string
  liabilityClickable: boolean
  liabilityType: BannerType
}

interface PracticeLicenseBannerData {
  showLicenseBanner: boolean
  licenseText: string
}

interface ProviderInfo {
  providerLicenses: Array<{
    licenseStatusDisplayId?: string
  }>
  liabilityInsurances: Array<{
    liabilityInsuranceStatusDisplayId?: string
  }>
}

interface PracticeLiabilityInsurance {
  insuranceStatus: string
}

/**
 * Calculates liability banner data for practices with group policies
 */
export const getPracticeGroupPolicyBannerData = (
  liabilityInsurances: PracticeLiabilityInsurance[] | undefined,
  publicProfileContent: PublicProfileSlideContent,
): PracticeLiabilityBannerData => {
  const insuranceStatus = getAggregateInsuranceStatus(
    (liabilityInsurances ?? []).map((ins) => ({
      liabilityInsuranceStatusDisplayId: ins.insuranceStatus,
    })),
  )
  if (insuranceStatus !== LiabilityStatusEnum.ACTIVE) {
    let statusText = ""
    switch (insuranceStatus) {
      case LiabilityStatusEnum.EXPIRED:
        statusText =
          publicProfileContent?.practiceLiabilityExpiredBannerText || ""
        break
      case LiabilityStatusEnum.PENDING:
        statusText =
          publicProfileContent?.practiceLiabilityPendingBannerText || ""
        break
      case LiabilityStatusEnum.EXPIRING_SOON:
        statusText =
          publicProfileContent?.practiceLiabilityExpiringSoonBannerText || ""
        break
      case LiabilityStatusEnum.REQUIRED:
      case LiabilityStatusEnum.NOT_AVAILABLE:
        statusText =
          publicProfileContent?.practiceLiabilityRequiredBannerText || ""
        break
      default:
        statusText =
          publicProfileContent?.practiceLiabilityRequiredBannerText || ""
        break
    }
    return {
      showLiabilityBanner: true,
      liabilityText: statusText,
      liabilityClickable: false,
      liabilityType: getLiabilityBannerType(insuranceStatus),
    }
  }

  return {
    showLiabilityBanner: false,
    liabilityText: "",
    liabilityClickable: false,
    liabilityType: "error" as BannerType,
  }
}

/**
 * Calculates liability banner data for practices without group policies (provider-level)
 */
export const getProviderLiabilityBannerData = (
  providersInfo: ProviderInfo[] | undefined,
  bannerText: string,
): PracticeLiabilityBannerData => {
  const anyProviderMissingActive =
    !!providersInfo &&
    providersInfo.some((p) =>
      p.liabilityInsurances.every(
        (ins) =>
          ins.liabilityInsuranceStatusDisplayId !== LiabilityStatusEnum.ACTIVE,
      ),
    )

  if (anyProviderMissingActive) {
    return {
      showLiabilityBanner: true,
      liabilityText: bannerText,
      liabilityClickable: true,
      liabilityType: "error" as BannerType,
    }
  }

  return {
    showLiabilityBanner: false,
    liabilityText: "",
    liabilityClickable: true,
    liabilityType: "error" as BannerType,
  }
}

/**
 * Calculates license banner data for practices based on provider licenses
 */
export const getPracticeLicenseBannerData = (
  providersInfo: ProviderInfo[] | undefined,
  bannerText: string,
): PracticeLicenseBannerData => {
  const anyProviderLicenses =
    (providersInfo || []).map((p) => p.providerLicenses).flat() || []

  const showLicense = anyProviderLicenses.some(
    (lic) =>
      lic.licenseStatusDisplayId === LicenseStatusEnum.EXPIRING_SOON ||
      lic.licenseStatusDisplayId === LicenseStatusEnum.EXPIRED,
  )

  return {
    showLicenseBanner: showLicense,
    licenseText: showLicense ? bannerText : "",
  }
}

/**
 * Gets the liability badge text based on status
 */
export const getLiabilityBadgeText = (
  status: LiabilityStatusEnum,
  publicProfileContent: PublicProfileSlideContent,
): string => {
  let statusText = publicProfileContent?.liability || ""
  switch (status) {
    case LiabilityStatusEnum.REQUIRED:
    case LiabilityStatusEnum.NOT_AVAILABLE:
      return statusText + " " + (publicProfileContent?.statusRequiredText || "")
    case LiabilityStatusEnum.EXPIRED:
      return statusText + " " + (publicProfileContent?.statusExpiredText || "")
    case LiabilityStatusEnum.PENDING:
      return statusText + " " + (publicProfileContent?.statusPendingText || "")
    case LiabilityStatusEnum.EXPIRING_SOON:
      return (
        statusText + " " + (publicProfileContent?.statusExpiringSoonText || "")
      )
    default:
      return statusText + " " + (publicProfileContent?.statusRequiredText || "")
  }
}

/**
 * Gets the badge color based on liability status
 * Maps to Badge color prop values
 */
export const getLiabilityBadgeColor = (
  status: LiabilityStatusEnum,
): "purple" | "red" => {
  return status === LiabilityStatusEnum.PENDING ? "purple" : "red"
}

export interface PracticeAgreementBannerData {
  showPracticeAgreementBanner: boolean
  practiceAgreementText: string
}

export const FALLBACK_PRACTICE_AGREEMENT_BANNER_TEXT =
  "Practice agreement signature is required."

export const getPracticeAgreementBannerData = (
  isSignatureRequired: boolean,
  publicProfileContent: PublicProfileSlideContent | undefined,
): PracticeAgreementBannerData => {
  return {
    showPracticeAgreementBanner: isSignatureRequired,
    practiceAgreementText: isSignatureRequired
      ? publicProfileContent?.practiceAgreementSignatureRequiredBannerText ||
        FALLBACK_PRACTICE_AGREEMENT_BANNER_TEXT
      : "",
  }
}
